import {
    Button,
    Stack,
    Typography,
    Box,
    Tooltip,
} from "@mui/material";
import { UploadFile } from '@mui/icons-material';
import { useState, useEffect, useCallback, useRef } from "react";
import AddNewEmployee from "./AddNewEmployee/AddNewEmployee";
import SetupTable from "../PayrollSetup/SetupTable";
import CustomPagination from "../../Components/CustomPagination";
import { getFilteredEmpDetails, deleteEmployee } from "../../api";
import EmployeeImport from "./EmployeeImport";
import PayrollsPopup from "../../Components/PayrollPopup";
import shortcutManager from "../../Utilities/ShortcutManager";
import { useParams } from "react-router-dom";
import { FILTER_OPTION_MAP } from "../../Enums/Enum";
import { useAlerts, usePermissions } from "../../Hooks/index";
import { SnackbarProvider } from "notistack";
import { Loader } from "../../Components/Loader";
import { employeeManagementStyles } from "../../styles/employeeManagement.styles";

// --- Table Columns Definition ---
const columns = [
    { name: 'Corporation', dataKey: 'corporation', width: 150 },
    { name: 'Employee Name', dataKey: 'employeeName', width: 150 },
    { name: 'Email ID', dataKey: 'email', width: 150 },
    { name: 'Mobile Num', dataKey: 'mobile', width: 150 },
    { name: 'Status', dataKey: 'status', width: 150 }
];

const createFilter = (filter) => ({
    CriterionID: filter?.id || "",
    SearchTerm: filter?.term || "",
    FilterOption: filter?.term ? (FILTER_OPTION_MAP[filter.option] ?? 1) : 4
});

const buildEmployeePayload = (filters, corpId, page) => {
    let corpSearch = createFilter(filters.corporation);
    if (corpId && corpId !== "000000000000000000000000000000000000" && !filters.corporation?.term) {
        corpSearch = { CriterionID: corpId, SearchTerm: "", FilterOption: 0 };
    }
    return {
        CorpSearch: corpSearch,
        EmpSearch: createFilter(filters.employeeName),
        EmailSearch: createFilter(filters.email),
        MobileSearch: createFilter(filters.mobile),
        StatusSearch: createFilter(filters.status),
        PageNum: page
    };
};

const formatEmployeeRows = (dataList) => {
    if (!dataList) return [];
    return dataList.map(row => ({
        id: row.ID,
        corporation: row.CorporationName,
        employeeName: row.EmployeeName,
        email: row.EmailID,
        mobile: row.MobileNum,
        status: row.IsActive === 1 ? "active" : "inactive",
        raw: row
    }));
};

/**
 * Employee Management Directory Page.
 * Displays filterable paginated employee list with edit, delete, and import actions.
 */
const EmployeeManagement = () => {
    const [isAddEmployeeOpen, setIsAddEmployeeOpen] = useState(false);
    const [isImportOpen, setIsImportOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const snackbarRef = useRef(null);
    const [fetchLoading, setFetchLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [tableRows, setTableRows] = useState([]);
    const [filters, setFilters] = useState({});
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [refreshKey, setRefreshKey] = useState(0);
    const [error, setError] = useState(null);
    const { corpId, menuId } = useParams();

    // React-scoped bounded cache (max 50 entries) - prevents cross-tenant leaks & unbounded memory
    const employeeTableCacheRef = useRef({});

    const { popup: alertPopup, showDeleteConfirm, showErrorPopup, hidePopup } = useAlerts();
    const { permissions, loading: permissionsLoading } = usePermissions(menuId);

    const showErrorSnackbar = useCallback((message) => {
        snackbarRef.current?.enqueueSnackbar(message, {
            variant: "error",
        });
    }, []);

    const showSuccessSnackbar = useCallback((message) => {
        snackbarRef.current?.enqueueSnackbar(message, {
            variant: "success",
        });
    }, []);

    const isGridLoading = permissionsLoading || fetchLoading;

    // Reset cache when corporation changes
    useEffect(() => {
        employeeTableCacheRef.current = {};
    }, [corpId]);

    // --- Utility Handlers ---
    const handleRefresh = useCallback(() => {
        setFetchLoading(true);
        setFilters({});
        setPage(0);
        employeeTableCacheRef.current = {};
        setRefreshKey(prev => prev + 1);
    }, []);

    const closeAddEmployee = useCallback(() => {
        setIsAddEmployeeOpen(false);
        setEditId(null);
        setRefreshKey(prev => prev + 1);
    }, []);

    const closeImport = useCallback(() => {
        setIsImportOpen(false);
        setRefreshKey(prev => prev + 1);
    }, []);

    const handleActionSuccess = useCallback(() => {
        employeeTableCacheRef.current = {};
        setPage(0);
        setRefreshKey(prev => prev + 1);
    }, []);

    const showErrorPopupRef = useRef(showErrorPopup);
    useEffect(() => {
        showErrorPopupRef.current = showErrorPopup;
    }, [showErrorPopup]);

    // --- Data Fetching Effect (Immediate on Mount/Page/Refresh, Debounced on Filter Input) ---
    useEffect(() => {
        if (permissionsLoading) return;

        let isCancelled = false;
        const hasFilters = Object.values(filters || {}).some(f => Boolean(f?.term));
        const delay = hasFilters ? 400 : 0;

        const fetchData = async () => {
            setFetchLoading(true);
            setError(null);

            const payload = buildEmployeePayload(filters, corpId, page);
            const cacheKey = JSON.stringify({
                page,
                corpId,
                filters: {
                    corporation: filters?.corporation?.term || "",
                    employeeName: filters?.employeeName?.term || "",
                    email: filters?.email?.term || "",
                    mobile: filters?.mobile?.term || "",
                    status: filters?.status?.term || ""
                }
            });

            try {
                let response;
                if (employeeTableCacheRef.current[cacheKey]) {
                    response = employeeTableCacheRef.current[cacheKey];
                } else {
                    response = await getFilteredEmpDetails(payload);

                    // Evict all entries if cache exceeds 50 to prevent memory growth
                    if (Object.keys(employeeTableCacheRef.current).length > 50) {
                        employeeTableCacheRef.current = {};
                    }
                    employeeTableCacheRef.current[cacheKey] = response;
                }

                if (isCancelled) return;

                const dataList = response?.EmpDetails || (Array.isArray(response) ? response : (response?.Data || []));

                if (Array.isArray(dataList) && dataList.length > 0) {
                    const totalRecords = dataList[0]?.Totalcount || dataList[0]?.TotalRecords || 0;
                    const pageSize = dataList[0]?.MaxRows || 40;
                    setTotalPages(Math.ceil(totalRecords / pageSize) || 1);
                } else {
                    setTotalPages(1);
                }

                setTableRows(formatEmployeeRows(dataList));
            } catch {
                if (isCancelled) return;
                const msg = "Failed to load employees. Please try again.";
                setError(msg);
                if (showErrorPopupRef.current) {
                    showErrorPopupRef.current(msg);
                }
                setTableRows([]);
            } finally {
                if (!isCancelled) setFetchLoading(false);
            }
        };

        let timer;
        if (delay > 0) {
            timer = setTimeout(fetchData, delay);
        } else {
            fetchData();
        }

        return () => {
            isCancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [filters, page, refreshKey, corpId, permissionsLoading]);

    // --- Delete Employee Action ---
    const handleDeleteEmployee = useCallback((row) => {
        if (!row?.id) return;

        showDeleteConfirm(`Are you sure you want to delete ${row.employeeName}?`, async () => {
            try {
                setActionLoading(true);
                const response = await deleteEmployee(row.id);
                if (response?.StatusCode === 200 || response?.statusCode === 200) { 
                    showSuccessSnackbar(response?.Status || "Employee deleted successfully");
                    employeeTableCacheRef.current = {};
                    setRefreshKey(prev => prev + 1);
                } else { 
                    showErrorSnackbar(response?.Status || "Failed to delete employee");
                }
            } catch {
                showErrorSnackbar("Something went wrong while deleting employee");
            } finally {
                setActionLoading(false);
            }
        });
    }, [showDeleteConfirm, showSuccessSnackbar, showErrorSnackbar]);

    // --- Keyboard Shortcuts ---
    useEffect(() => {
        shortcutManager.addShortcut("alt+f5", handleRefresh);
        return () => shortcutManager.removeShortcut("alt+f5");
    }, [handleRefresh]);

    // Parent Window Menu Toggling
    useEffect(() => {
        if (isAddEmployeeOpen) {
            window.parent.postMessage("HideMenu", "*");
        } else {
            window.parent.postMessage("ShowMenu", "*");
        }
    }, [isAddEmployeeOpen]);

    return (
        <SnackbarProvider
            ref={snackbarRef}
            maxSnack={1}
            anchorOrigin={{
                vertical: "top",
                horizontal: "right",
            }}
            autoHideDuration={2000}
            style={{ zIndex: 99999 }}
        >
            <>
                {fetchLoading && <Loader loading={fetchLoading} />}
                {actionLoading && <Loader loading={actionLoading} />}

                <Stack height={"100%"} gap={0.5} p={0}>
                    <Stack alignSelf={"flex-end"} direction="row" gap={1} alignItems="center">
                        <Tooltip title="Import">
                            <UploadFile
                                aria-label="Import Employees"
                                onClick={() => {
                                    if (!permissions.canCreate) {
                                        showErrorSnackbar("You don't have permission to import employees.");
                                        return;
                                    }
                                    setIsImportOpen(true);
                                }}
                                sx={employeeManagementStyles.uploadIcon}
                            />
                        </Tooltip>
                        <Button
                            size="small"
                            aria-label="Add New Employee"
                            sx={employeeManagementStyles.addNewButton}
                            variant="contained"
                            disableElevation
                            color="secondary"
                            onClick={() => {
                                if (!permissions.canCreate) {
                                    showErrorSnackbar("You don't have permission to add employees.");
                                    return;
                                }
                                setIsAddEmployeeOpen(true);
                            }}
                        >
                            Add New
                        </Button>
                    </Stack>

                    {error ? (
                        <Stack flexGrow={1} alignItems="center" justifyContent="center" gap={2} sx={employeeManagementStyles.errorContainer}>
                            <Typography color="error" variant="body1" fontWeight={500}>{error}</Typography>
                            <Typography color="textSecondary" variant="body2">Please check your connection and try again.</Typography>
                            <Button variant="contained" color="primary" disableElevation sx={employeeManagementStyles.retryButton} onClick={handleRefresh}>Retry</Button>
                        </Stack>
                    ) : (
                        <Stack flexGrow={1} sx={employeeManagementStyles.tableWrapperStack}>
                            <Box sx={employeeManagementStyles.tableWrapperBox}>
                                <SetupTable
                                    rows={tableRows}
                                    columns={columns}
                                    filters={filters}
                                    loading={isGridLoading}
                                    onFilterChange={(newFilters) => {
                                        employeeTableCacheRef.current = {};
                                        setFilters(newFilters);
                                        setPage(0);
                                    }}
                                    onEdit={(row) => {
                                        if (!permissions.canUpdate) {
                                            showErrorSnackbar("You don't have permission to edit employees.");
                                            return;
                                        }

                                        setEditId(row.id);
                                        setIsAddEmployeeOpen(true);
                                    }}
                                    onDelete={(row) => {
                                        if (!permissions.canDelete) {
                                            showErrorSnackbar("You don't have permission to delete employees.");
                                            return;
                                        }
                                        handleDeleteEmployee(row);
                                    }}
                                />
                            </Box>
                            <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={employeeManagementStyles.paginationFooter}>
                                <CustomPagination
                                    count={totalPages}
                                    page={page}
                                    onChange={(e, newPage) => setPage(newPage - 1)}
                                />
                            </Stack>
                        </Stack>
                    )}
                </Stack>

                {isAddEmployeeOpen && (
                    <AddNewEmployee
                        open={isAddEmployeeOpen}
                        fullScreen
                        onClose={closeAddEmployee}
                        onSuccess={handleActionSuccess}
                        employeeId={editId}
                        permissions={permissions}
                    />
                )}

                {isImportOpen && (
                    <EmployeeImport
                        open={isImportOpen}
                        onClose={closeImport}
                        onSuccess={handleActionSuccess}
                    />
                )}

                <PayrollsPopup
                    open={alertPopup.open}
                    popupType={alertPopup.type}
                    message={alertPopup.message}
                    onConfirm={alertPopup.onConfirm}
                    onCancel={hidePopup}
                />
            </>
        </SnackbarProvider>
    );
};

export default EmployeeManagement;
